(() => {
  'use strict';
  if (customElements.get('fs-live-quickview')) return;

  let pendingHistory = null;
  let activeHistoryOwner = null;
  // Presentation cache; collection purchase context has its own bounded lifetime.
  const presentationCache = new Map();
  const presentationTTL = 60000;
  const presentationLimit = 12;
  const id = (value) => String(value ?? '');
  const canonical = (value) => Array.isArray(value) ? value.map(canonical) : value && typeof value === 'object' ? Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonical(value[key])])) : value;
  const lineIdentity = (line) => JSON.stringify(canonical([id(line.variant_id || line.id), line.properties || {}, line.selling_plan_allocation?.selling_plan?.id || null, line.parent_relationship || null, line.parent_id || null, line.item_components || [], line.components || [], Boolean(line.remote)]));
  const quantities = (cart) => {
    if (!Array.isArray(cart?.items)) throw new Error('Invalid cart');
    const result = new Map();
    cart.items.forEach((line) => {
      if (!Number.isSafeInteger(line.quantity) || line.quantity < 1) throw new Error('Invalid quantity');
      const key = lineIdentity(line);
      result.set(key, (result.get(key) || 0) + line.quantity);
    });
    return JSON.stringify([...result].sort(([a], [b]) => a.localeCompare(b)));
  };
  const smallSize = (value) => ({ xs: 'XS', extrasmall: 'XS', xsmall: 'XS', s: 'S', small: 'S', m: 'M', medium: 'M', l: 'L', large: 'L', xl: 'XL', extralarge: 'XL', xlarge: 'XL', xxl: 'XXL', xxlarge: 'XXL', extraextralarge: 'XXL' }[String(value).trim().toLowerCase().replace(/[\s_-]+/g, '')] || String(value));
  const variantLabel = (product, variant) => {
    const names = (product.options || []).map((option) => typeof option === 'string' ? option : option.name);
    const options = variant.options || [variant.option1, variant.option2, variant.option3].filter((value) => value != null);
    return options.map((value, index) => {
      const name = names[index] || '';
      const label = /^sizes?$/i.test(name.trim()) ? smallSize(value) : value;
      return names.length > 1 && name ? name + ': ' + label : label;
    }).join(' · ') || variant.title;
  };
  const stock = (variant) => variant.available !== true ? 'SoldOut' : variant.inventory_management && String(variant.inventory_policy).toLowerCase() === 'continue' && Number.isFinite(variant.inventory_quantity) && variant.inventory_quantity <= 0 ? 'Backorder' : 'Available';
  const simple = (product, variant) => !product.requires_selling_plan && !variant.requires_selling_plan && !product.requires_components && !variant.requires_components && !product.requires_properties && !product.selling_plan_groups?.length && !variant.selling_plan_allocations?.length && Number.isSafeInteger(Number(variant.id)) && Number(variant.id) > 0 && Number.isSafeInteger(variant.price) && variant.price >= 0 && (variant.quantity_rule?.min ?? 1) === 1 && (variant.quantity_rule?.increment ?? 1) === 1;
  const safeImage = (value, base) => {
    try {
      const url = new URL(value || '', base);
      return value && url.protocol === 'https:' ? url.href : '';
    } catch (_) { return ''; }
  };
  const imageSize = (image) => {
    const width = Number(image?.naturalWidth || image?.width);
    const height = Number(image?.naturalHeight || image?.height);
    return Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0 ? { width: Math.round(width), height: Math.round(height) } : {};
  };
  const sameImage = (left, right) => {
    try {
      const a = new URL(left, location.origin);
      const b = new URL(right, location.origin);
      return a.origin === b.origin && a.pathname === b.pathname && a.searchParams.get('v') === b.searchParams.get('v');
    } catch (_) { return false; }
  };
  const allowedTags = new Set(['P', 'DIV', 'SECTION', 'SPAN', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'BR', 'HR', 'UL', 'OL', 'LI', 'DL', 'DT', 'DD', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TH', 'TD', 'CAPTION', 'FIGURE', 'FIGCAPTION', 'IMG', 'BLOCKQUOTE', 'SUP', 'SUB']);
  const blockedTags = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'FORM', 'INPUT', 'SELECT', 'TEXTAREA', 'BUTTON', 'TEMPLATE', 'NOSCRIPT', 'SVG', 'MATH', 'LINK', 'META']);
  const cardSelector = '[data-fs-live-compact-card], .card-wrapper, [data-fhd-card], [data-fhg-card]';
  const gridSelector = '[data-fs-quickview-grid], [data-fsic-grid], #product-grid, .product-grid, [data-fhd-grid]';

  class FsLiveQuickview extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.owner = this.closest('[data-fs-live-collection]');
      this.dialog = this.querySelector('dialog');
      if (!this.owner || !this.dialog) return;
      this.connected = true;
      this.closeButton = this.el('close');
      this.image = this.el('image');
      this.photos = [];
      this.carousel = this.querySelector('.fs-live-quickview__photo');
      this.carousel.setAttribute('data-fs-product-carousel', '');
      this.noImageText = this.el('no-image').textContent;
      this.onPhotoScroll = () => {
        const width = this.carousel.clientWidth;
        if (!width || !this.photos.length) return;
        const index = Math.max(0, Math.min(this.photos.length - 1, Math.round(this.carousel.scrollLeft / width)));
        this.selectPhoto(index);
      };
      this.onOwnerClick = (event) => {
        const opener = event.target?.closest?.('[data-fs-live-quickview]');
        if (!opener || !this.owner.contains(opener) || event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || (event.button != null && event.button !== 0) || opener.hasAttribute('download') || (opener.target && opener.target !== '_self') || typeof this.dialog.showModal !== 'function') return;
        event.preventDefault();
        this.open(opener);
      };
      this.onClick = (event) => {
        const close = event.target.closest('[data-fs-product-close], [data-fs-product-back]');
        if (close) this.close();
        const photo = event.target.closest('[data-fs-product-photo]');
        if (photo) this.showPhoto(Number(photo.dataset.fsProductPhoto));
        if (event.target.closest('[data-fs-product-prev]')) this.showPhoto(this.photoIndex - 1);
        if (event.target.closest('[data-fs-product-next]')) this.showPhoto(this.photoIndex + 1);
        const browse = event.target.closest('[data-fs-product-browse-prev], [data-fs-product-browse-next]');
        if (browse && !browse.disabled) this.browseProduct(browse.hasAttribute('data-fs-product-browse-next') ? 1 : -1, browse);
        if (event.target.closest('[data-fs-product-retry]') && !this.navigationBusy()) this.open(this.opener);
        const variant = event.target.closest('[data-fs-product-variant]');
        if (variant && !variant.disabled) this.chooseVariant(variant.dataset.fsProductVariant);
        if (event.target.closest('[data-fs-product-add]')) this.add();
        if (event.target === this.dialog && this.backdropStart) {
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
        }
        this.backdropStart = false;
      };
      this.onPointerDown = (event) => { this.backdropStart = event.target === this.dialog; };
      this.onCancel = (event) => { event.preventDefault(); this.close(); };
      this.onClose = () => {
        if (this.dialog.open) return;
        this.controller?.abort();
        this.releaseHistory();
        this.unlockScroll();
        // Browsing changes the displayed product, never the place the shopper left.
        const opener = this.visibleOpener(this.returnOpener) ? this.returnOpener : this.visibleProducts(this.navigationGrid).find((item) => item.productId === this.returnProductId)?.opener;
        const fallback = [...this.owner.querySelectorAll('fs-live-size-filter button:not([disabled]), a[href]')].find((node) => this.visibleOpener(node));
        (opener || fallback)?.focus({ preventScroll: true });
      };
      this.onKeyDown = (event) => {
        if (event.key !== 'Tab') return;
        const focusable = Array.from(this.dialog.querySelectorAll('button:not([disabled]), [href], summary, [tabindex]:not([tabindex="-1"])')).filter((node) => !node.closest('[hidden]') && node.getClientRects().length);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const current = this.ownerDocument.activeElement;
        if (!first) { event.preventDefault(); this.closeButton.focus(); return; }
        if (event.shiftKey && (current === first || !this.dialog.contains(current))) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (current === last || !this.dialog.contains(current))) { event.preventDefault(); first.focus(); }
      };
      this.owner.addEventListener('click', this.onOwnerClick);
      this.owner.setAttribute('data-fs-live-quickview-ready', '');
      if (typeof window.MutationObserver === 'function') {
        this.navigationObserver = new window.MutationObserver((records) => {
          if (this.dialog.open && records.some((record) => !this.contains(record.target))) this.updateNavigation();
        });
        this.navigationObserver.observe(this.owner, { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden', 'class', 'style', 'aria-hidden', 'data-fs-live-quickview', 'data-product-url'] });
      }
      this.carousel.addEventListener('scroll', this.onPhotoScroll, { passive: true });
      if (typeof ResizeObserver === 'function') {
        this.carouselWidth = 0;
        this.carouselObserver = new ResizeObserver(() => {
          const width = this.carousel.clientWidth;
          if (width > 0 && width !== this.carouselWidth) {
            this.carouselWidth = width;
            this.alignPhoto();
          }
        });
        this.carouselObserver.observe(this.carousel);
      }
      this.dialog.addEventListener('click', this.onClick);
      this.dialog.addEventListener('pointerdown', this.onPointerDown);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('close', this.onClose);
      this.dialog.addEventListener('keydown', this.onKeyDown);

    }

    disconnectedCallback() {
      this.controller?.abort();
      this.releaseHistory();
      this.unlockScroll();
      this.owner?.removeEventListener('click', this.onOwnerClick);
      this.owner?.removeAttribute('data-fs-live-quickview-ready');
      this.carousel?.removeEventListener('scroll', this.onPhotoScroll);
      this.carouselObserver?.disconnect();
      this.navigationObserver?.disconnect();
      if (this.dialog) {
        this.dialog.removeEventListener('click', this.onClick);
        this.dialog.removeEventListener('pointerdown', this.onPointerDown);
        this.dialog.removeEventListener('cancel', this.onCancel);
        this.dialog.removeEventListener('close', this.onClose);
        this.dialog.removeEventListener('keydown', this.onKeyDown);
        if (this.dialog.open) this.dialog.close();
      }
      this.connected = false;
    }

    el(name) { return this.querySelector('[data-fs-product-' + name + ']'); }
    label(name) { return this.dataset[name] || ''; }
    money(value) {
      return new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || 'PKR' }).format(value / 100);
    }
    close() { if (this.dialog.open) this.dialog.close(); }
    visibleOpener(opener) {
      if (!opener?.isConnected || !this.owner.contains(opener) || this.contains(opener) || opener.closest('[hidden], [aria-hidden="true"]')) return false;
      if (!opener.getClientRects().length) return false;
      const style = window.getComputedStyle?.(opener);
      return !style || (style.visibility !== 'hidden' && style.visibility !== 'collapse');
    }
    visibleProducts(grid = this.navigationGrid) {
      if (!grid?.isConnected || !this.owner.contains(grid)) return [];
      const products = new Map();
      grid.querySelectorAll('[data-fs-live-quickview]').forEach((opener) => {
        const productId = id(opener.dataset.fsLiveQuickview);
        if (!/^\d+$/.test(productId) || !this.visibleOpener(opener)) return;
        // Stay in this grid/rail, including when the owner contains multiple grids.
        if (grid !== this.owner && opener.closest(gridSelector) !== grid) return;
        try { this.productURL({ url: opener.dataset.productUrl }, opener); } catch (_) { return; }
        const previous = products.get(productId);
        if (!previous || (previous.opener.hasAttribute('data-fs-quickview-purchase') && !opener.hasAttribute('data-fs-quickview-purchase'))) products.set(productId, { productId, opener });
      });
      return [...products.values()];
    }
    navigationBusy() {
      const root = this.ownerDocument.documentElement;
      return Boolean(this.pending || this.selectionController()?.pending || root.hasAttribute('data-fs-quickview-cart-pending') || root.hasAttribute('data-fs-selection-cart-pending'));
    }
    updateNavigation() {
      const navigation = this.el('navigation');
      if (!navigation) return;
      const products = this.visibleProducts();
      const index = products.findIndex((item) => item.productId === this.productId);
      navigation.hidden = products.length < 2 || index < 0;
      const busy = this.navigationBusy();
      this.el('browse-prev').disabled = busy || index <= 0;
      this.el('browse-next').disabled = busy || index < 0 || index >= products.length - 1;
      const position = index < 0 ? '' : this.label('labelProductPosition').replace('{current}', String(index + 1)).replace('{total}', String(products.length));
      if (this.el('browse-position').textContent !== position) this.el('browse-position').textContent = position;
    }
    browseProduct(direction, button) {
      if (!this.dialog.open || this.navigationBusy() || ![-1, 1].includes(direction)) return;
      const products = this.visibleProducts();
      const index = products.findIndex((item) => item.productId === this.productId);
      const next = index >= 0 && products[index + direction];
      if (!next) { this.updateNavigation(); return; }
      return this.open(next.opener, { browsing: true, focusTarget: button });
    }
    acquireHistory() {
      const history = window.history;
      activeHistoryOwner = this;
      if (!history?.pushState || this.historyKey) return;
      const state = history.state;
      if (pendingHistory && state?.__fsQuickview === pendingHistory.key) { this.historyAwaiting = true; return; }
      if (state != null && (typeof state !== 'object' || Array.isArray(state))) return;
      try {
        this.historyBaseState = JSON.stringify(state);
        this.historyURL = location.href;
        this.historyKey = 'fs-qv-' + Date.now() + '-' + Math.random().toString(36).slice(2);
        history.pushState({ ...state, __fsQuickview: this.historyKey }, '', location.href);
      } catch (_) { this.historyKey = null; }
    }
    releaseHistory() {
      this.historyAwaiting = false;
      if (activeHistoryOwner === this) activeHistoryOwner = null;
      if (!this.historyKey) return;
      const owned = window.history?.state?.__fsQuickview === this.historyKey;
      if (owned) pendingHistory = { key: this.historyKey, url: this.historyURL, state: this.historyBaseState };
      this.historyKey = null;
      if (owned) window.history.back();
    }
    lockScroll() {
      if (this.scrollLock) return;
      const body = this.ownerDocument.body;
      const html = this.ownerDocument.documentElement;
      this.scrollLock = { body: body.style.overflow, html: html.style.overflow, x: window.scrollX, y: window.scrollY };
      body.style.overflow = 'hidden';
      html.style.overflow = 'hidden';
    }
    unlockScroll() {
      if (!this.scrollLock) return;
      this.ownerDocument.body.style.overflow = this.scrollLock.body;
      this.ownerDocument.documentElement.style.overflow = this.scrollLock.html;
      const { x, y } = this.scrollLock;
      this.scrollLock = null;
      if (Number.isFinite(x) && Number.isFinite(y)) window.scrollTo?.({ left: x, top: y, behavior: 'instant' });
    }
    productURL(product, opener) {
      const url = new URL(product.url || opener.dataset.productUrl || '', location.origin);
      if (url.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) throw new Error('Invalid product URL');
      url.pathname = url.pathname.replace(/\/$/, '');
      url.searchParams.delete('preview_theme_id');
      url.hash = '';
      const preview = this.dataset.previewThemeId;
      if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
      const variant = opener.dataset.variant;
      if (variant && /^\d+$/.test(variant)) url.searchParams.set('variant', variant);
      return url;
    }
    async request(url, signal, format) {
      const response = await fetch(url.href, { method: 'GET', credentials: 'same-origin', cache: 'no-store', signal, headers: { Accept: format === 'json' ? 'application/json' : 'text/html' } });
      if (!response.ok) throw new Error('Product details unavailable');
      return format === 'json' ? response.json() : response.text();
    }
    cardSnapshot(opener, productId) {
      const card = opener.closest(cardSelector);
      const identity = card?.getAttribute('data-fs-live-compact-card') || card?.dataset.productId || card?.dataset.fhgCard || card?.dataset.fhdCard || card?.querySelector('[data-fs-live-quickview]')?.dataset.fsLiveQuickview;
      const source = card && id(identity) === productId ? card : opener;
      const image = opener.querySelector('img') || source.querySelector('.fs-live-compact-card__photo img, .card__media img, .fhd__front');
      const price = source.querySelector('.fs-live-compact-card__amount, [data-fhd-price]') || source.querySelector('.price--on-sale .price-item--sale, .price:not(.price--on-sale) .price-item--regular');
      return {
        title: opener.dataset.productTitle || '',
        price: price?.textContent.trim() || '',
        photo: { src: safeImage(image?.currentSrc || image?.getAttribute('src') || opener.dataset.productImage, location.origin), alt: image?.alt || opener.dataset.productTitle || '', ...imageSize(image) }
      };
    }
    async presentation(url, signal, format, productId) {
      const key = [productId, this.dataset.locale, this.dataset.currency, format, url.href].join('|');
      const now = Date.now();
      for (const [storedKey, entry] of presentationCache) {
        if (entry.expires <= now) { presentationCache.delete(storedKey); entry.controller.abort(); }
      }
      let entry = presentationCache.get(key);
      if (!entry) {
        const controller = new AbortController();
        entry = { controller, expires: now + presentationTTL };
        const timer = setTimeout(() => controller.abort(), 15000);
        entry.promise = this.request(url, controller.signal, format).then((data) => {
          if (controller.signal.aborted) throw new Error('Product details timed out');
          if (format === 'json') {
            if (!data || id(data.id) !== productId || !Array.isArray(data.images)) throw new Error('Mismatched product');
          } else {
            const main = new DOMParser().parseFromString(data, 'text/html').querySelector('product-info[id^="MainProduct-"]');
            if (!main || id(main.dataset.productId) !== productId) throw new Error('Mismatched product details');
          }
          entry.expires = Date.now() + presentationTTL;
          return data;
        }).catch((error) => {
          if (presentationCache.get(key) === entry) presentationCache.delete(key);
          throw error;
        }).finally(() => clearTimeout(timer));
        presentationCache.set(key, entry);
        while (presentationCache.size > presentationLimit) {
          const oldest = presentationCache.keys().next().value;
          const discarded = presentationCache.get(oldest);
          presentationCache.delete(oldest);
          discarded.controller.abort();
        }
      } else {
        presentationCache.delete(key);
        presentationCache.set(key, entry);
      }
      // Closing a view stops its consumer, not a request another open can reuse.
      return new Promise((resolve, reject) => {
        const abort = () => { cleanup(); reject(new Error('Product view closed')); };
        const cleanup = () => signal.removeEventListener('abort', abort);
        signal.addEventListener('abort', abort, { once: true });
        entry.promise.then((value) => { cleanup(); if (!signal.aborted) resolve(value); }, (error) => { cleanup(); reject(error); });
        if (signal.aborted) abort();
      });
    }
    async open(opener, options = {}) {
      if (!opener || typeof this.dialog.showModal !== 'function') return;
      if (options.browsing && this.navigationBusy()) return;
      const productId = String(opener.dataset.fsLiveQuickview || '');
      if (!/^\d+$/.test(productId)) return;
      if (!this.dialog.open) {
        this.returnOpener = opener;
        this.returnProductId = productId;
        this.navigationGrid = opener.closest(gridSelector) || this.owner;
      }
      const snapshot = this.cardSnapshot(opener, productId);
      const product = { id: productId, title: snapshot.title, url: opener.dataset.productUrl || '' };
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.opener = opener;
      this.productId = productId;
      const session = { productId, selected: null, product: null, safe: false, url: null };
      this.session = session;
      this.el('purchase').hidden = true;
      this.el('variants').replaceChildren();
      this.el('purchase-status').textContent = '';
      this.el('cart-link').hidden = true;
      this.el('cart-summary').textContent = '';
      this.updatePurchase();
      this.el('title').textContent = product.title;
      this.el('price').textContent = snapshot.price;
      this.el('status').textContent = this.label('labelLoading');
      this.el('retry').hidden = true;
      this.el('empty').hidden = true;
      this.el('description').hidden = true;
      this.el('description-content').replaceChildren();
      this.el('details').replaceChildren();
      this.el('link').removeAttribute('href');
      this.el('content').setAttribute('aria-busy', 'true');
      this.descriptionLoaded = false;
      this.detailsLoaded = false;
      this.setPhotos(snapshot.photo.src ? [snapshot.photo] : []);
      if (!this.dialog.open) {
        this.dialog.showModal();
        this.lockScroll();
        this.acquireHistory();
      }
      this.alignPhoto();
      this.el('scroll').scrollTop = 0;
      this.updateNavigation();
      const focusTarget = options.browsing ? [options.focusTarget, this.el('browse-next'), this.el('browse-prev')].find((button) => button && !button.disabled && !button.closest('[hidden]')) : null;
      (focusTarget || this.closeButton).focus({ preventScroll: true });
      let timer;
      try {
        const url = this.productURL(product, opener);
        const selection = this.selectionController();
        if (selection) url.searchParams.delete('variant');
        this.el('link').href = url.href;
        session.url = url;
        const jsonURL = new URL(url.href);
        jsonURL.pathname += '.js';
        const controller = this.controller;
        timer = setTimeout(() => controller.abort(), 15000);
        const current = () => !signal.aborted && this.dialog.open && this.controller === controller;
        const results = await Promise.allSettled([
          this.presentation(jsonURL, signal, 'json', productId).then((data) => {
            if (!current()) return;
            if (!data || String(data.id) !== productId || !Array.isArray(data.images)) throw new Error('Mismatched product');
            this.el('title').textContent = String(data.title || product.title);
            const photoMeta = new Map((data.media || []).filter((media) => media.media_type === 'image' && media.preview_image?.src).map((media) => [safeImage(media.preview_image.src, url), { ...media.preview_image, alt: media.alt }]));
            this.setPhotos(data.images.map((image, index) => {
              const src = typeof image === 'string' ? image : image.src;
              const meta = photoMeta.get(safeImage(src, url));
              const preview = snapshot.photo.src && (index === 0 || sameImage(snapshot.photo.src, src)) ? snapshot.photo : null;
              return { src, alt: (typeof image === 'object' && image.alt) || meta?.alt || data.title || product.title, ...imageSize(meta || image), ...(preview ? { previewSrc: preview.src, ...imageSize(preview) } : {}) };
            }));
            session.jsonReady = true;
            session.requiresComponents = Boolean(data.requires_components || (data.variants || []).some((variant) => variant.requires_components));
            const variant = null;
            const price = variant ? variant.price : data.price_min ?? data.price;
            if (Number.isSafeInteger(price) && price >= 0 && typeof this.money === 'function') {
              const max = !variant && Number.isSafeInteger(data.price_max) && data.price_max > price ? ' – ' + this.money(data.price_max) : '';
              this.el('price').textContent = this.money(price) + max;
            }
            if (data.description) this.setDescription(data.description, url);
          }),
          this.presentation(url, signal, 'html', productId).then((html) => {
            if (!current()) return;
            const page = new DOMParser().parseFromString(html, 'text/html');
            const main = page.querySelector('product-info[id^="MainProduct-"]');
            if (!main || String(main.dataset.productId) !== productId) throw new Error('Mismatched product details');
            session.safe = !main.querySelector('[name^="properties["], [name="selling_plan"], [data-recipient-form], [data-requires-properties]');
            session.pageReady = true;
            if (!this.descriptionLoaded) {
              const description = main.querySelector('.product__description');
              if (description) this.setDescription(description.innerHTML, url);
            }
            const blocks = [];
            main.querySelectorAll('.product__accordion').forEach((block) => {
              const title = block.querySelector('.accordion__title')?.textContent.trim();
              const content = block.querySelector('.accordion__content');
              if (title && content) blocks.push({ title, content });
            });
            main.querySelectorAll('.product-popup-modal__content-info').forEach((content) => {
              const heading = content.querySelector('h1, h2, h3');
              const title = heading?.textContent.trim();
              if (!title) return;
              heading.remove();
              blocks.push({ title, content });
            });
            const seen = new Set();
            blocks.forEach(({ title, content }) => {
              const key = title + ':' + content.textContent.trim();
              if (seen.has(key)) return;
              seen.add(key);
              const fragment = this.sanitize(content, url);
              if (!fragment.textContent.trim() && !fragment.querySelector('img')) return;
              const details = this.ownerDocument.createElement('details');
              details.className = 'fs-live-quickview__detail';
              const summary = this.ownerDocument.createElement('summary');
              summary.textContent = title;
              const body = this.ownerDocument.createElement('div');
              body.className = 'fs-live-quickview__rich';
              body.append(fragment);
              details.append(summary, body);
              this.el('details').append(details);
            });
            this.detailsLoaded = true;
          }),
          (selection ? selection.contextFor(url, productId) : this.loadPurchaseProduct(url, signal)).then((data) => {
            if (!current()) return;
            if (id(data.id) !== productId) throw new Error('Mismatched purchase product');
            session.product = data;
          })
        ]);
        if (!this.dialog.open || this.controller !== controller) return;
        if (!signal.aborted && this.session === session) {
          // Replace any card/cached display price with this open's fresh purchase prices.
          const prices = session.product?.variants.map((variant) => variant.price);
          if (prices?.length && prices.every((price) => Number.isSafeInteger(price) && price >= 0)) {
            const min = Math.min(...prices);
            const max = Math.max(...prices);
            this.el('price').textContent = this.money(min) + (max > min ? ' – ' + this.money(max) : '');
          }
          this.renderVariants();
          if (!options.browsing && opener.hasAttribute('data-fs-quickview-purchase')) {
            (this.el('variants').querySelector('button:not([disabled])') || this.el('link')).focus({ preventScroll: true });
            this.el('purchase').scrollIntoView?.({ block: 'nearest', behavior: 'instant' });
          }
        }
        const failed = results.some((result) => result.status === 'rejected');
        this.el('status').textContent = failed ? this.label('labelError') : '';
        this.el('retry').hidden = !failed;
        this.el('empty').hidden = failed || this.descriptionLoaded || Boolean(this.el('details').children.length);
      } catch (_) {
        if (signal.aborted || !this.dialog.open) return;
        this.el('status').textContent = this.label('labelError');
        this.el('retry').hidden = false;
      } finally {
        clearTimeout(timer);
        if (this.controller?.signal === signal) this.el('content').setAttribute('aria-busy', 'false');
      }
    }
    async loadPurchaseProduct(url, signal) {
      const endpoint = new URL(url.href);
      endpoint.searchParams.delete('variant');
      endpoint.searchParams.set('view', 'fs-inline-data');
      const html = await this.request(endpoint, signal, 'html');
      const page = new DOMParser().parseFromString(html, 'text/html');
      const data = JSON.parse(page.querySelector('#fs-inline-data')?.textContent || 'null');
      if (data?.schema !== 1 || data.kind !== 'product' || data.complete !== true || !Array.isArray(data.product?.variants) || data.currency !== this.dataset.currency) throw new Error('Purchase data unavailable');
      return data.product;
    }
    purchaseStatus(name) { this.el('purchase-status').textContent = this.label('label' + name); }
    blocked() { return this.ownerDocument.documentElement.hasAttribute('data-fs-quickview-cart-uncertain'); }
    cartBusy() {
      const document = this.ownerDocument;
      return document.documentElement.hasAttribute('data-fs-quickview-cart-pending') || document.documentElement.hasAttribute('data-fs-live-cart-checkout-locked') || Boolean(document.querySelector('.cart__items--disabled')) || [...document.querySelectorAll('[data-fs-hoodie-grid]')].some((root) => (root.fhg?.pending || root.fhg?.blocked));
    }
    variantPresentation(product) {
      // Both purchase surfaces use the same labels, without changing variant identity.
      const names = (product.options || []).map((option) => String(typeof option === 'string' ? option : option.name));
      const rows = product.variants.map((variant) => ({ variant, values: variant.options || [variant.option1, variant.option2, variant.option3].filter((value) => value != null) }));
      const size = (index) => /^sizes?$/i.test(names[index].trim());
      const constant = names.map((_, index) => names.length > 1 && !size(index) && rows.length > 0 && rows[0].values[index] != null && rows.every((row) => row.values[index] === rows[0].values[index]));
      // Keep at least one option visible even for a single-variant, non-size product.
      if (constant.every(Boolean)) constant[0] = false;
      const axes = names.map((_, index) => index).filter((index) => !constant[index]).sort((a, b) => Number(size(b)) - Number(size(a)));
      const labels = new Map(rows.map(({ variant, values }) => [id(variant.id), axes.map((index) => size(index) ? smallSize(values[index] ?? '') : String(values[index] ?? '')).join(' · ') || variant.title]));
      // Two genuinely different custom values must never collapse to one short label.
      const counts = new Map(); labels.forEach((label) => counts.set(label, (counts.get(label) || 0) + 1));
      rows.forEach(({ variant, values }) => { if (counts.get(labels.get(id(variant.id))) > 1) labels.set(id(variant.id), values.map((value, index) => names[index] ? names[index] + ': ' + value : value).join(' · ') || variant.title); });
      return { labels, context: names.map((name, index) => constant[index] ? name + ': ' + rows[0].values[index] : '').filter(Boolean).join(' · ') };
    }
    renderVariantNotes(list, presentation, details = []) {
      // Stock and fixed color belong outside the 44px size targets.
      list.parentElement.querySelector('[data-fs-option-context]')?.remove();
      list.parentElement.querySelector('[data-fs-option-notes]')?.remove();
      if (!presentation) return;
      if (presentation.context) {
        const context = this.ownerDocument.createElement('p');
        context.className = 'fs-variant-context'; context.dataset.fsOptionContext = ''; context.textContent = presentation.context;
        list.before(context);
      }
      const grouped = new Map();
      details.forEach(({ label, detail }) => { if (detail) { if (!grouped.has(detail)) grouped.set(detail, []); grouped.get(detail).push(label); } });
      if (!grouped.size) return;
      const notes = this.ownerDocument.createElement('div'); notes.className = 'fs-variant-notes'; notes.dataset.fsOptionNotes = '';
      grouped.forEach((labels, detail) => { const line = this.ownerDocument.createElement('p'); line.textContent = detail + ': ' + labels.join(', '); notes.append(line); });
      list.after(notes);
    }
    renderVariants() {
      const { product, safe, pageReady } = this.session || {};
      const supported = product && safe && pageReady && this.session.jsonReady && !this.session.requiresComponents && !product.requires_selling_plan && !product.requires_components && product.variants.some((variant) => simple(product, variant));
      this.session.supported = Boolean(supported);
      this.el('purchase').hidden = false;
      this.el('variants').replaceChildren();
      this.renderVariantNotes(this.el('variants'), null);
      this.el('variant-legend').hidden = !supported;
      if (!supported) {
        this.purchaseStatus('FullDetails');
        this.updatePurchase();
        return;
      }
      const presentation = this.variantPresentation(product); const details = [];
      this.el('variants').classList.add('fs-variant-choices');
      product.variants.forEach((variant) => {
        const button = this.ownerDocument.createElement('button');
        button.type = 'button';
        button.dataset.fsProductVariant = id(variant.id);
        button.className = 'fs-live-quickview__variant fs-variant-chip';
        button.textContent = presentation.labels.get(id(variant.id));
        button.dataset.fsStock = stock(variant);
        const detail = this.label('label' + (simple(product, variant) ? stock(variant) : 'FullDetails'));
        if (!simple(product, variant) || stock(variant) !== 'Available') details.push({ label: button.textContent, detail });
        button.disabled = variant.available !== true || !simple(product, variant);
        button.setAttribute('aria-pressed', String(id(this.session.selected?.id) === id(variant.id)));
        button.setAttribute('aria-label', variantLabel(product, variant) + ' · ' + detail);
        this.el('variants').append(button);
      });
      this.renderVariantNotes(this.el('variants'), presentation, details);
      if (!this.session.selected) {
        if (this.selectionController()) this.el('purchase-status').textContent = this.selectionController().text(this.selectionController().blocked ? 'cart_uncertain' : 'choose_size');
        else this.purchaseStatus(this.blocked() ? 'Uncertain' : 'Choose');
      }
      this.updatePurchase();
    }
    selectionRequired() { return Boolean(this.owner?.querySelector('[data-fs-collection-selection]')); }
    selectionController() {
      const controller = this.owner?.fsCollectionSelection;
      return controller?.root?.isConnected && controller.owner === this.owner && controller.quickview === this ? controller : null;
    }
    updatePurchase() {
      this.updateNavigation();
      const button = this.el('add');
      if (!button) return;
      button.hidden = this.selectionRequired();
      button.disabled = Boolean(this.pending || this.blocked() || (this.selectionRequired() && !this.selectionController()) || !this.session?.selected || !this.session?.supported);
      button.setAttribute('aria-busy', String(Boolean(this.pending)));
      this.el('add-label').textContent = this.pending ? this.label('labelChecking') : this.label('labelAdd');
      this.el('variants')?.querySelectorAll('button').forEach((button) => {
        const variant = this.session?.product?.variants.find((variant) => id(variant.id) === button.dataset.fsProductVariant);
        button.disabled = Boolean(this.pending || this.selectionController()?.pending || this.selectionController()?.blocked || (this.selectionRequired() && !this.selectionController()) || !variant || variant.available !== true || !simple(this.session.product, variant));
        button.setAttribute('aria-busy', String(this.selectionController()?.pendingVariantId === button.dataset.fsProductVariant));
      });
      if (this.blocked()) this.el('cart-link').hidden = false;
    }
    chooseVariant(variantId) {
      if (this.pending || this.blocked()) return;
      const session = this.session;
      const variant = session?.product?.variants.find((variant) => id(variant.id) === id(variantId));
      if (!session.supported || !variant || variant.available !== true || !simple(session.product, variant)) return;
      session.selected = { ...variant };
      this.el('variants').querySelectorAll('button').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.fsProductVariant === id(variant.id))));
      this.el('price').textContent = this.money(variant.price);
      this.el('purchase-status').textContent = variantLabel(session.product, variant) + ' · ' + this.label('label' + stock(variant));
      const link = new URL(session.url.href); link.searchParams.set('variant', id(variant.id)); this.el('link').href = link.href;
      this.updatePurchase();
      const selection = this.selectionController();
      if (selection) return selection.addFromQuickview(this, this.el('variants').querySelector(`[data-fs-product-variant="${id(variant.id)}"]`));
    }
    cartEndpoint(path) {
      const url = new URL(path, location.origin);
      if (url.origin !== location.origin) throw new Error('Invalid cart endpoint');
      url.pathname = url.pathname.replace(/\/$/, '').replace(/\.js$/, '') + '.js';
      url.search = ''; url.hash = '';
      return url.href;
    }
    async cartRequest(url, options = {}) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), options.method === 'POST' ? 20000 : 12000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
        const data = await response.json();
        if (!response.ok || data?.status >= 400 || data?.errors) { const error = new Error('Cart rejected'); error.status = response.status; error.description = typeof data?.description === 'string' ? data.description.slice(0, 500) : ''; throw error; }
        return data;
      } finally { clearTimeout(timer); }
    }
    async add() {
      const selection = this.selectionController();
      if (selection) return selection.addFromQuickview(this);
      if (this.selectionRequired()) return;
      const session = this.session;
      if (this.pending || this.blocked() || !session?.selected || !session.supported) return;
      if (this.cartBusy()) { this.purchaseStatus('Busy'); return; }
      const document = this.ownerDocument;
      const selected = { ...session.selected };
      const root = document.documentElement;
      this.pending = true;
      root.setAttribute('data-fs-quickview-cart-pending', '');
      this.purchaseStatus('Checking'); this.updatePurchase();
      let attempted = false;
      let verifiedRead = false;
      let before;
      let finished = false;
      const current = () => this.session === session && this.dialog.open && this.isConnected;
      const status = (name) => { if (current()) this.purchaseStatus(name); };
      const uncertain = () => { root.setAttribute('data-fs-quickview-cart-uncertain', ''); status('Uncertain'); if (current()) this.el('cart-link').hidden = false; };
      try {
        const selectedPageURL = new URL(session.url.href);
        selectedPageURL.searchParams.set('variant', id(selected.id));
        const [fresh, freshPage] = await Promise.all([
          this.loadPurchaseProduct(session.url, this.controller.signal),
          this.request(selectedPageURL, this.controller.signal, 'html')
        ]);
        if (!current()) return;
        if (id(fresh.id) !== session.productId) throw new Error('Mismatched product');
        const main = new DOMParser().parseFromString(freshPage, 'text/html').querySelector('product-info[id^="MainProduct-"]');
        if (!main || id(main.dataset.productId) !== session.productId) throw new Error('Mismatched product form');
        if (main.querySelector('[name^="properties["], [name="selling_plan"], [data-recipient-form], [data-requires-properties]')) {
          session.safe = false; session.selected = null; this.renderVariants(); return;
        }
        const variant = fresh.variants.find((variant) => id(variant.id) === id(selected.id));
        session.product = fresh;
        if (!variant || variant.available !== true || !simple(fresh, variant)) {
          session.selected = null; this.renderVariants(); status('Unavailable'); return;
        }
        const changed = variant.price !== selected.price || variantLabel(fresh, variant) !== variantLabel(fresh, selected) || stock(variant) !== stock(selected);
        session.selected = { ...variant };
        this.renderVariants();
        this.el('price').textContent = this.money(variant.price);
        if (changed) { status('Changed'); return; }
        before = await this.cartRequest(this.cartEndpoint(this.dataset.cartUrl));
        quantities(before);
        if (before.currency && before.currency !== this.dataset.currency) throw new Error('Currency changed');
        if (!current()) return;
        const inCart = before.items.filter((line) => id(line.variant_id || line.id) === id(selected.id)).reduce((sum, line) => sum + line.quantity, 0);
        if ((variant.quantity_rule?.max && inCart + 1 > variant.quantity_rule.max) || (variant.inventory_management && String(variant.inventory_policy).toLowerCase() === 'deny' && Number.isFinite(variant.inventory_quantity) && inCart + 1 > variant.inventory_quantity)) { status('Quantity'); return; }
        const drawer = document.querySelector('cart-drawer');
        const sections = drawer?.getSectionsToRender?.() || [{ id: 'cart-icon-bubble' }];
        const sectionIds = [...new Set(['cart-icon-bubble', ...sections.map((section) => section.id)])].slice(0, 5);
        const body = { items: [{ id: Number(selected.id), quantity: 1 }], sections: sectionIds, sections_url: location.pathname };
        let response; let rejected;
        attempted = true;
        try { response = await this.cartRequest(this.cartEndpoint(this.dataset.cartAddUrl), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }); }
        catch (error) { rejected = error; }
        const after = await this.cartRequest(this.cartEndpoint(this.dataset.cartUrl));
        const afterQuantities = quantities(after);
        verifiedRead = true;
        if (rejected) {
          if ([400, 404, 422].includes(rejected.status) && quantities(before) === afterQuantities) { status('CartError'); return; }
          uncertain(); return;
        }
        const expected = { items: [...before.items, { variant_id: selected.id, quantity: 1 }] };
        if (quantities(expected) !== afterQuantities) { uncertain(); return; }
        finished = true;
        if (current()) {
          session.selected = null;
          this.renderVariants();
          status('Success');
          this.el('cart-link').hidden = false;
          if (Number.isSafeInteger(after.item_count) && Number.isSafeInteger(after.total_price)) this.el('cart-summary').textContent = this.label('labelBagSummary').replace('[count]', String(after.item_count)).replace('[total]', this.money(after.total_price));
          try { this.animateBag(); } catch (_) {  }
        }
        try { await this.syncCart(after, response, sections, sectionIds); }
        catch (_) { status('SuccessReview'); }
      } catch (_) {
        if (attempted && !finished) uncertain();
        else if (!finished) status('PurchaseError');
      } finally {
        if (!attempted || verifiedRead || this.blocked()) root.removeAttribute('data-fs-quickview-cart-pending');
        this.pending = false;
        this.updatePurchase();
      }
    }
    async syncCart(after, response, definitions, sectionIds, options = {}) {
      let sections = response?.sections;
      if (!sectionIds.every((key) => typeof sections?.[key] === 'string')) {
        const url = new URL(location.href); url.searchParams.set('sections', sectionIds.join(','));
        sections = await this.cartRequest(url.href);
      }
      const definitionsById = new Map(definitions.map((section) => [section.id, section]));
      definitionsById.set('cart-icon-bubble', { id: 'cart-icon-bubble' });
      const updates = [...definitionsById.values()].map((section) => {
        const source = new DOMParser().parseFromString(sections[section.id] || '', 'text/html').querySelector(section.selector || '.shopify-section');
        const target = section.selector ? this.ownerDocument.querySelector(section.selector) : this.ownerDocument.getElementById(section.id);
        if (!source) throw new Error('Missing section');
        return { source, target, section };
      });
      updates.forEach(({ source, target }) => { if (target) target.innerHTML = source.innerHTML; });
      const drawer = this.ownerDocument.querySelector('cart-drawer');
      if (drawer) {
        drawer.classList.toggle('is-empty', after.item_count === 0);
        drawer.querySelector('.drawer__inner')?.classList.toggle('is-empty', after.item_count === 0);
        drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'fs-live-quickview',
        ...(options.summaryOnly ? { cartSummary: after } : { cartData: after }),
        // Only the matching drawer consumer can skip a refresh; other sections still update.
        sectionsApplied: updates.filter(({ target, section }) => target && section.id === 'cart-drawer' && section.selector === '#CartDrawer').map(({ section }) => section.id)
      });
    }
    animateBag() {
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || typeof this.image?.animate !== 'function') return;
      const target = this.el('cart-link').getBoundingClientRect();
      const source = this.image.getBoundingClientRect();
      if (!source.width || !source.height || !target.width || !target.height || target.bottom <= 0 || target.top >= window.innerHeight || target.right <= 0 || target.left >= window.innerWidth) return;
      const flyer = this.ownerDocument.createElement('img');
      flyer.className = 'fs-live-quickview__flight'; flyer.src = this.image.currentSrc || this.image.src; flyer.alt = ''; flyer.setAttribute('aria-hidden', 'true');
      const left = Math.max(8, Math.min(window.innerWidth - 56, source.left + source.width / 2 - 24));
      const top = Math.max(8, Math.min(window.innerHeight - 68, source.top + source.height / 2 - 30));
      Object.assign(flyer.style, { left: left + 'px', top: top + 'px' });
      this.dialog.append(flyer);
      const dx = target.left + target.width / 2 - left - 24;
      const dy = target.top + target.height / 2 - top - 30;
      const cleanup = () => flyer.remove();
      setTimeout(cleanup, 1200);
      try {
        const animation = flyer.animate([{ transform: 'translate(0, 0) scale(1)', opacity: 1 }, { transform: `translate(${dx * .4}px, ${dy * .4 - 24}px) scale(1.03)`, opacity: 1, offset: .4 }, { transform: `translate(${dx}px, ${dy}px) scale(.12)`, opacity: .15 }], { duration: 650, easing: 'cubic-bezier(.3,.6,.3,1)' });
        animation.finished.then(cleanup, cleanup);
      } catch (_) { cleanup(); }
    }

    setPhotos(photos) {
      this.photos = photos.map((photo) => ({ ...photo, src: safeImage(photo.src, location.origin) })).filter((photo) => photo.src);
      this.photoImages = [];
      this.carousel.replaceChildren();
      this.photoIndex = 0;
      if (!this.photos.length) {
        const empty = this.ownerDocument.createElement('p');
        empty.dataset.fsProductNoImage = '';
        empty.textContent = this.noImageText;
        this.carousel.append(empty);
      }
      this.photos.forEach((photo, index) => {
        const slide = this.ownerDocument.createElement('div');
        slide.className = 'fs-live-quickview__slide';
        slide.dataset.fsProductSlide = String(index);
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-label', this.photoLabel(index));
        const img = this.ownerDocument.createElement('img');
        const size = imageSize(photo);
        const applySize = (dimensions) => {
          if (!dimensions.width || !dimensions.height) return;
          img.width = dimensions.width;
          img.height = dimensions.height;
          img.style.setProperty('--fs-quickview-image-ratio', dimensions.width + ' / ' + dimensions.height);
        };
        applySize(size);
        img.addEventListener('load', () => applySize(imageSize(img)));
        img.src = safeImage(photo.previewSrc, location.origin) || photo.src;
        img.alt = photo.alt || this.el('title').textContent;
        img.loading = index === 0 ? 'eager' : 'lazy';
        if (index === 0) img.setAttribute('fetchpriority', 'high');
        if (photo.previewSrc && photo.previewSrc !== photo.src) {
          const upgrade = this.ownerDocument.createElement('img');
          upgrade.addEventListener('load', () => {
            if (!img.isConnected) return;
            applySize(imageSize(upgrade));
            img.src = photo.src;
          }, { once: true });
          upgrade.src = photo.src;
        }
        img.draggable = false;
        const fallback = this.ownerDocument.createElement('p');
        fallback.dataset.fsProductNoImage = '';
        fallback.textContent = this.noImageText;
        fallback.hidden = true;
        img.addEventListener('error', () => { img.hidden = true; fallback.hidden = false; }, { once: true });
        slide.append(img, fallback);
        this.carousel.append(slide);
        this.photoImages.push(img);
      });
      const thumbs = this.el('thumbs');
      thumbs.replaceChildren();
      this.photos.forEach((photo, index) => {
        const button = this.ownerDocument.createElement('button');
        button.type = 'button';
        button.dataset.fsProductPhoto = String(index);
        button.setAttribute('aria-label', this.photoLabel(index));
        const img = this.ownerDocument.createElement('img');
        img.src = photo.src;
        img.alt = '';
        img.width = 72;
        img.height = 90;
        img.loading = 'lazy';
        button.append(img);
        thumbs.append(button);
      });
      thumbs.hidden = this.photos.length < 2;
      this.el('photo-controls').hidden = this.photos.length < 2;
      this.showPhoto(0);
    }
    photoLabel(index) { return this.label('labelPhoto').replace('{current}', String(index + 1)).replace('{total}', String(this.photos.length)); }
    selectPhoto(index) {
      if (!this.photos.length) {
        this.image = null;
        this.el('counter').textContent = '';
        return;
      }
      this.photoIndex = Math.max(0, Math.min(this.photos.length - 1, index));
      this.photoImages.forEach((image, position) => {
        if (position === this.photoIndex) image.setAttribute('data-fs-product-image', '');
        else image.removeAttribute('data-fs-product-image');
      });
      this.image = this.photoImages[this.photoIndex];
      this.el('counter').textContent = this.photoLabel(this.photoIndex);
      this.el('thumbs').querySelectorAll('button').forEach((button, position) => button.setAttribute('aria-pressed', String(position === this.photoIndex)));
    }
    alignPhoto() {
      const width = this.carousel.clientWidth;
      if (!width || !this.photos.length) return;
      const left = width * this.photoIndex;
      if (typeof this.carousel.scrollTo === 'function') this.carousel.scrollTo({ left, behavior: 'auto' });
      else this.carousel.scrollLeft = left;
    }
    showPhoto(index) {
      const next = this.photos.length ? (index + this.photos.length) % this.photos.length : 0;
      this.selectPhoto(next);
      this.alignPhoto();
    }
    setDescription(html, base) {
      const source = new DOMParser().parseFromString(html, 'text/html');
      const fragment = this.sanitize(source.body, base);
      if (!fragment.textContent.trim() && !fragment.querySelector('img')) return;
      this.el('description-content').replaceChildren(fragment);
      this.el('description').hidden = false;
      this.descriptionLoaded = true;
    }
    sanitize(source, base) {
      const output = this.ownerDocument.createDocumentFragment();
      const copy = (node, parent) => {
        if (node.nodeType === 3) { parent.append(this.ownerDocument.createTextNode(node.textContent)); return; }
        if (node.nodeType !== 1 || blockedTags.has(node.tagName)) return;
        if (!allowedTags.has(node.tagName)) { Array.from(node.childNodes).forEach((child) => copy(child, parent)); return; }
        const tag = /^H[1-6]$/.test(node.tagName) ? 'h4' : node.tagName.toLowerCase();
        const element = this.ownerDocument.createElement(tag);
        if (tag === 'img') {
          const src = safeImage(node.getAttribute('src') || node.getAttribute('data-src'), base);
          if (!src) return;
          element.src = src;
          element.alt = node.getAttribute('alt') || '';
          element.loading = 'lazy';
          element.decoding = 'async';
          ['width', 'height'].forEach((attribute) => {
            const value = node.getAttribute(attribute);
            if (/^\d{1,5}$/.test(value || '')) element.setAttribute(attribute, value);
          });
        }
        if (tag === 'td' || tag === 'th') {
          ['colspan', 'rowspan'].forEach((attribute) => {
            const value = node.getAttribute(attribute);
            if (/^[1-9]\d?$/.test(value || '')) element.setAttribute(attribute, value);
          });
          if (['row', 'col', 'rowgroup', 'colgroup'].includes(node.getAttribute('scope'))) element.setAttribute('scope', node.getAttribute('scope'));
        }
        Array.from(node.childNodes).forEach((child) => copy(child, element));
        parent.append(element);
      };
      Array.from(source.childNodes).forEach((node) => copy(node, output));
      return output;
    }
  }
  window.addEventListener('popstate', (event) => {
    const state = window.history?.state;
    let serialized;
    try { serialized = JSON.stringify(state); } catch (_) { serialized = null; }
    const returned = pendingHistory && location.href === pendingHistory.url && serialized === pendingHistory.state;
    if (returned) {
      pendingHistory = null;
      event.stopImmediatePropagation();
      if (activeHistoryOwner?.historyAwaiting && activeHistoryOwner.dialog.open) {
        activeHistoryOwner.historyAwaiting = false;
        activeHistoryOwner.acquireHistory();
      }
      return;
    }
    const owner = activeHistoryOwner;
    if (!owner?.dialog.open || state?.__fsQuickview === owner.historyKey) return;
    const ownedReturn = owner.historyKey && location.href === owner.historyURL && serialized === owner.historyBaseState;
    if (ownedReturn) event.stopImmediatePropagation();
    owner.historyKey = null;
    activeHistoryOwner = null;
    owner.close();
  }, true);
  customElements.define('fs-live-quickview', FsLiveQuickview);
})();
